import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useMutation } from '@tanstack/react-query'
import { Turnstile } from '@marsidev/react-turnstile'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, Mail, ArrowLeft, CheckCircle2, ShieldCheck } from 'lucide-react'
import { getErrorMessage } from '@/lib/utils'
import { authApi } from '../api/authApi'
import { useState, useRef, useEffect } from 'react'
import { env } from '@/config/env'

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

export function ForgotPassword() {
  const [isSuccess, setIsSuccess] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState('')
  const [showTurnstileDialog, setShowTurnstileDialog] = useState(false)
  const [turnstileStatus, setTurnstileStatus] = useState<'pending' | 'verifying' | 'success' | 'error'>('pending')
  const pendingEmail = useRef<string | null>(null)

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  })

  const resetMutation = useMutation({
    // The platform's own reset, not Supabase's — and the reason is worth keeping.
    //
    // Routing this at Supabase was correct in intent (its links expire, are single-use and
    // rate-limited, and resetting was meant to migrate an artist onto the new identity) but it
    // silently did nothing for every artist on the platform. Supabase can only recover an account
    // it holds, and no artist has one: they live in this application's `users` table and are
    // linked to Supabase on first sign-in, which for a dormant artist never happened. Supabase
    // answers "unknown email" with success so attackers cannot enumerate accounts, so the page
    // said "check your email" and no email was ever sent. An artist reported it twice before
    // anyone could see it; her account had been dormant since 2021.
    //
    // The API path works for everyone, linked or not, and it is what sends the mail. The Supabase
    // reset comes back when artists actually have accounts there and the project has SMTP
    // configured — see docs/auth/PASSWORD_RESET.md.
    mutationFn: async ({ email, turnstileToken }: { email: string; turnstileToken?: string }) => {
      return authApi.sendResetInstructions(email, turnstileToken)
    },
    onSuccess: () => {
      setIsSuccess(true)
      setShowTurnstileDialog(false)
    },
    onError: () => {
      setShowTurnstileDialog(false)
      setTurnstileStatus('pending')
    },
  })

  const onSubmit = (data: ForgotPasswordFormData) => {
    setSubmittedEmail(data.email)
    
    if (env.turnstileSiteKey) {
      // Store email and show verification dialog
      pendingEmail.current = data.email
      setTurnstileStatus('verifying')
      setShowTurnstileDialog(true)
    } else {
      // No Turnstile configured, submit directly
      resetMutation.mutate({ email: data.email })
    }
  }

  // Handle Turnstile success
  const handleTurnstileSuccess = (token: string) => {
    setTurnstileStatus('success')
    // Small delay to show success state before submitting
    setTimeout(() => {
      if (pendingEmail.current) {
        resetMutation.mutate({ email: pendingEmail.current, turnstileToken: token })
      }
    }, 500)
  }

  // Handle Turnstile error
  const handleTurnstileError = () => {
    setTurnstileStatus('error')
  }

  // Reset dialog state when closed
  const handleDialogClose = (open: boolean) => {
    if (!open) {
      setShowTurnstileDialog(false)
      setTurnstileStatus('pending')
      pendingEmail.current = null
    }
  }

  // Close dialog if error occurs
  useEffect(() => {
    if (resetMutation.error) {
      setShowTurnstileDialog(false)
      setTurnstileStatus('pending')
    }
  }, [resetMutation.error])

  // Success state - email sent
  if (isSuccess) {
    return (
      <div className="w-full">
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
            <CheckCircle2 className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="font-serif text-4xl font-normal tracking-[0.2em] mb-4">Arthaus</h1>
          <h2 className="text-2xl font-semibold mb-2">Check Your Email</h2>
          <p className="text-muted-foreground text-sm max-w-sm mx-auto">
            We've sent password reset instructions to{' '}
            <span className="font-medium text-foreground">{submittedEmail}</span>
          </p>
        </div>

        <div className="space-y-4">
          <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
            <p className="mb-2">
              <strong className="text-foreground">Didn't receive the email?</strong>
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>Check your spam or junk folder</li>
              <li>Make sure you entered the correct email</li>
              <li>Wait a few minutes and try again</li>
            </ul>
          </div>

          <Button
            variant="outline"
            className="w-full h-12"
            onClick={() => {
              setIsSuccess(false)
              setSubmittedEmail('')
              resetMutation.reset()
              form.reset()
            }}
          >
            Try a different email
          </Button>

          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="font-serif text-4xl font-normal tracking-[0.2em] mb-4">Arthaus</h1>
        <h2 className="text-2xl font-semibold mb-2">Forgot Password?</h2>
        <p className="text-muted-foreground text-sm">
          Enter your email and we'll send you instructions to reset your password.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          {resetMutation.error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{getErrorMessage(resetMutation.error)}</AlertDescription>
            </Alert>
          )}

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium text-foreground">
                  Email Address
                </FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      {...field}
                      disabled={resetMutation.isPending}
                      className="pl-10 h-12"
                      autoFocus
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="w-full h-12 text-base font-medium"
            disabled={resetMutation.isPending}
          >
            {resetMutation.isPending ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Sending...
              </>
            ) : (
              'Send Reset Instructions'
            )}
          </Button>

          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to sign in
            </Link>
          </div>
        </form>
      </Form>

      {/* Turnstile Verification Dialog */}
      <Dialog open={showTurnstileDialog} onOpenChange={handleDialogClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Security Verification
            </DialogTitle>
            <DialogDescription>
              Please complete the security check to continue.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex flex-col items-center gap-4 py-4">
            {turnstileStatus === 'verifying' && (
              <div className="text-center">
                <div className="flex justify-center mb-3">
                  <Turnstile
                    siteKey={env.turnstileSiteKey}
                    onSuccess={handleTurnstileSuccess}
                    onError={handleTurnstileError}
                    onExpire={handleTurnstileError}
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Verifying you're human...
                </p>
              </div>
            )}

            {turnstileStatus === 'success' && (
              <div className="text-center py-4">
                <div className="flex justify-center mb-3">
                  <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                    <ShieldCheck className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <p className="text-sm font-medium text-green-600">Verification successful!</p>
                <p className="text-sm text-muted-foreground mt-1">Sending reset instructions...</p>
              </div>
            )}

            {turnstileStatus === 'error' && (
              <div className="text-center py-4">
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Verification failed. Please try again.
                  </AlertDescription>
                </Alert>
                <Button
                  variant="outline"
                  onClick={() => setTurnstileStatus('verifying')}
                  className="mr-2"
                >
                  Try Again
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => handleDialogClose(false)}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>

          {turnstileStatus === 'verifying' && (
            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDialogClose(false)}
              >
                Cancel and go back
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
